import React, { useState } from "react";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { removeCustomerVatIdExclusion } from "../../../services/clearing/customers";
import {
  VatIdExclusion,
  formatExclusionTimestamp,
  reasonLabel,
} from "../../../utils/vatIdExclusion";

interface VatIdExclusionDetailsProps {
  customerId: string;
  exclusion: VatIdExclusion;
  /**
   * Called once the customer is back on the worklist. Returns once the page shows the
   * updated customer, and rejects if it could not be refreshed.
   */
  onUndone: () => void | Promise<void>;
  toastRef: React.RefObject<Toast>;
}

/**
 * Explains, on the customer's own page, why they are not being chased for a VAT
 * ID (CLR-276). Without this the reason lives only in the Clearing overview, so
 * anyone looking at the customer sees an empty VAT ID and no explanation.
 */
export default function VatIdExclusionDetails({
  customerId,
  exclusion,
  onUndone,
  toastRef,
}: VatIdExclusionDetailsProps) {
  const [undoing, setUndoing] = useState(false);

  const handleUndo = async () => {
    setUndoing(true);
    try {
      await removeCustomerVatIdExclusion(customerId);
    } catch (e: any) {
      toastRef?.current?.show({
        severity: "error",
        summary: "Error",
        detail: e?.message || "Failed to put the customer back on the list.",
        life: 4000,
      });
      setUndoing(false);
      return;
    }

    // The removal worked; only the refresh can still fail, and that is said separately so
    // the stale status on the page is not presented as current.
    try {
      await onUndone();
      toastRef?.current?.show({
        severity: "success",
        summary: "Back on the list",
        detail: "This customer is on the missing VAT ID list again.",
        life: 4000,
      });
    } catch {
      toastRef?.current?.show({
        severity: "warn",
        summary: "Back on the list",
        detail:
          "The customer is on the missing VAT ID list again, but this page could not be refreshed. Reload it to see the current VAT.",
        life: 8000,
      });
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div
      className="mt-3 pt-3 border-top-1 surface-border"
      data-testid="vat-id-exclusion"
    >
      <p className="m-0 mb-2 flex align-items-center gap-2">
        <i className="pi pi-eye-slash text-color-secondary" />
        <strong>Excluded from the missing VAT ID list</strong>
      </p>

      <p className="m-0">
        <strong>Reason:</strong> {reasonLabel(exclusion.reason)}
      </p>
      {exclusion.note && (
        <p className="m-0">
          <strong>Note:</strong> {exclusion.note}
        </p>
      )}
      <p className="m-0">
        <strong>Removed by:</strong> {exclusion.excluded_by || "—"}
      </p>
      <p className="m-0">
        <strong>Removed on:</strong>{" "}
        {formatExclusionTimestamp(exclusion.excluded_at)}
      </p>

      <Button
        label="Put back on the list"
        icon="pi pi-undo"
        outlined
        className="mt-3"
        loading={undoing}
        onClick={handleUndo}
      />
    </div>
  );
}
